import { useCallback, useMemo } from 'react';
import {
  ButtonVariants,
  Dropdown,
  Field,
  FieldTypes,
  Form,
  FormSizes,
  Modal,
  ModalSizes,
} from '@carboledger/ui';
import { useGetMaterialsQuery } from '@/lib/api/lookupsApi';
import { EMPTY_LIST } from '@/lib/utils';
import { useTranslations } from '@/hooks';
import { useAssignSiteMaterialsMutation, useGetSiteDetailQuery } from '../../api/sitesApi';
import {
  ASSIGN_MATERIALS_FIELDS,
  EMPTY_ASSIGN_MATERIALS_FORM,
} from '../../constants/sites.constants';
import styles from './AssignMaterialsModal.module.scss';

const isAssignable = (values) =>
  Boolean(values[ASSIGN_MATERIALS_FIELDS.terminalId]) &&
  (values[ASSIGN_MATERIALS_FIELDS.materialIds] ?? []).length > 0;

/**
 * Props:
 *   open    whether the modal is shown
 *   onClose close handler (✕ / Cancel)
 *   siteId  the site being assigned to — its terminals and certification type scope the form
 */
export const AssignMaterialsModal = ({ open, onClose, siteId }) => {
  const t = useTranslations();

  const { data: site } = useGetSiteDetailQuery(siteId, { skip: !open });
  const { data: materials } = useGetMaterialsQuery(site?.certificateType ?? undefined, {
    skip: !open || !site,
  });
  const [assignSiteMaterials, { isLoading: saving }] = useAssignSiteMaterialsMutation();

  const terminalOptions = useMemo(
    () =>
      (site?.terminals ?? EMPTY_LIST).map((terminal) => ({
        value: String(terminal.id),
        label: terminal.terminalName,
      })),
    [site?.terminals],
  );

  const materialOptions = useMemo(
    () =>
      (materials ?? EMPTY_LIST).map((material) => ({ value: material.id, label: material.name })),
    [materials],
  );

  const handleSubmit = useCallback(
    async (values) => {
      const result = await assignSiteMaterials({ siteId, values });
      if (result.error) return;
      onClose();
    },
    [assignSiteMaterials, siteId, onClose],
  );

  if (!open) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={EMPTY_ASSIGN_MATERIALS_FORM}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ values, submit }) => (
        <Modal
          open
          size={ModalSizes.small}
          title={t('SITES_ACTION_ASSIGN_MATERIALS')}
          onClose={onClose}
          buttons={[
            { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
            {
              label: t('COMMON_ASSIGN'),
              variant: ButtonVariants.primary,
              disabled: saving || !isAssignable(values),
              onClick: submit,
            },
          ]}
        >
          <div className={styles.fields}>
            <Field name={ASSIGN_MATERIALS_FIELDS.terminalId} required>
              {({ id, name, value, onChange, onBlur, status, message, required }) => (
                <Dropdown
                  id={id}
                  name={name}
                  label={t('FORM_SELECT_TERMINAL')}
                  placeholder={t('FORM_SELECT_TERMINAL')}
                  emptyMessage={t('FORM_TERMINAL_EMPTY')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  status={status}
                  message={message}
                  required={required}
                  disabled={saving}
                  options={terminalOptions}
                />
              )}
            </Field>

            <Field name={ASSIGN_MATERIALS_FIELDS.materialIds} type={FieldTypes.multi} required>
              {({ id, name, value, onChange, status, message, required }) => (
                <Dropdown
                  multiple
                  id={id}
                  name={name}
                  label={t('FORM_SELECT_MATERIAL')}
                  placeholder={t('FORM_SELECT_MATERIALS')}
                  emptyMessage={t('MATERIALS_LIST_EMPTY')}
                  value={value}
                  onChange={onChange}
                  status={status}
                  message={message}
                  required={required}
                  disabled={saving}
                  options={materialOptions}
                />
              )}
            </Field>
          </div>
        </Modal>
      )}
    </Form>
  );
};
